import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import { useSEO } from "@/hooks/useSEO";
import { categoryList, regionList, sortList, videoList, yearList } from "@/mocks/videos";

const PAGE_SIZE = 12;

export default function Category() {
  const { type = "movie" } = useParams();
  const category = categoryList.find((item) => item.key === type);

  useSEO({
    title: `${category ? category.name : "影视"} - 桔子视频`,
    description: category
      ? `${category.description}，尽在桔子视频${category.name}频道，支持在线观看与视频分享。`
      : "桔子视频影视分类，支持在线观看与视频分享。",
    keywords: category
      ? `桔子视频,${category.name},在线观看,${category.name}推荐,影视资源`
      : "桔子视频,影视分类,在线观看",
  });

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState<number | "全部">("全部");
  const [sort, setSort] = useState("latest");
  const [genre, setGenre] = useState("全部");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const genres = useMemo(() => {
    const set = new Set<string>();
    videoList
      .filter((item) => item.typeKey === type)
      .forEach((item) => item.genres.forEach((g) => set.add(g)));
    return ["全部", ...Array.from(set)];
  }, [type]);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setGenre("全部");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [region, year, sort, genre]);

  const filtered = useMemo(() => {
    let list = videoList.filter((item) => item.typeKey === type);
    if (region !== "全部") list = list.filter((item) => item.region === region);
    if (year !== "全部") list = list.filter((item) => item.year === year);
    if (genre !== "全部") list = list.filter((item) => item.genres.includes(genre));
    if (sort === "latest") list = [...list].sort((a, b) => b.year - a.year);
    if (sort === "hot") list = [...list].sort((a, b) => b.rating - a.rating);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [type, region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setSort("latest");
    setGenre("全部");
    setPage(1);
  };

  if (!category) {
    return (
      <Layout>
        <div className="mx-auto max-w-[1280px] px-4 py-24 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-emotion-sad-line" />
          </span>
          <h1 className="mt-4 text-xl font-semibold text-foreground-900">分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">请从导航中选择一个有效的影视分类。</p>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const FilterGroup = ({
    label,
    options,
    value,
    onPick,
  }: {
    label: string;
    options: { value: string | number; label: string }[];
    value: string | number;
    onPick: (v: string | number) => void;
  }) => (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:gap-4">
      <span className="w-14 shrink-0 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onPick(option.value)}
            className={`cursor-pointer rounded-md px-3 py-1.5 text-sm transition-colors ${
              value === option.value
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />

        <div className="mt-5 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
            <i className={category.icon} />
          </span>
          <div>
            <h1 className="text-xl font-bold text-foreground-950 md:text-2xl font-heading">
              {category.name}
            </h1>
            <p className="mt-0.5 text-sm text-foreground-500">{category.description}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-6 max-w-[1280px] gap-8 px-4 md:px-6 lg:flex">
        <aside className="mb-6 shrink-0 lg:mb-0 lg:w-56">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground-900">分类导航</h2>
            <ul className="space-y-1">
              {categoryList.map((item) => (
                <li key={item.key}>
                  <Link
                    to={`/category/${item.key}`}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                      item.key === type
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <span className="flex h-5 w-5 items-center justify-center">
                      <i className={item.icon} />
                    </span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h2 className="mb-3 mt-6 text-sm font-semibold text-foreground-900">类型筛选</h2>
            <div className="flex flex-wrap gap-2">
              {genres.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGenre(g)}
                  className={`cursor-pointer rounded-md px-2.5 py-1 text-xs transition-colors ${
                    genre === g
                      ? "bg-accent-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-accent-100 hover:text-accent-700"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-2 md:px-5">
            <FilterGroup
              label="地区"
              options={regionList.map((r) => ({ value: r, label: r }))}
              value={region}
              onPick={(v) => setRegion(String(v))}
            />
            <div className="border-t border-background-200" />
            <FilterGroup
              label="年份"
              options={["全部", ...yearList].map((y) => ({
                value: y,
                label: y === "全部" ? "全部" : `${y}`,
              }))}
              value={year}
              onPick={(v) => setYear(v === "全部" ? "全部" : Number(v))}
            />
            <div className="border-t border-background-200" />
            <FilterGroup
              label="排序"
              options={sortList.map((s) => ({ value: s.key, label: s.label }))}
              value={sort}
              onPick={(v) => setSort(String(v))}
            />
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                <i className="ri-file-search-line" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-foreground-900">没有找到符合条件的视频</h2>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}